import {
    IAiModel,
    findModelByActionTag,
    getPublicModelCostWithDuration,
    getPrivateModelCostWithDuration,
    usdToSats,
} from 'state/aiModels'
import React from 'react'
import api from 'api/api'
import useStore from 'state/knovStore'
import { confirmAsync } from 'components/shared/confirmUtils'
import { getCachedWalletDetails, waitForWalletBalance } from 'lib/walletBalance'
import { BsvWalletType } from 'wallets/wallets'
import {
    cancelExternalCheckout,
    openExternalCheckout,
    prepareExternalCheckout,
} from 'utils/nativeApp'
import {
    getCachedStripeAiBillingStatus,
    isStripeAiBillingStatusCacheFresh,
    refreshStripeAiBillingStatus,
    setCachedStripeAiBillingPaymentMode,
    StripeAiBillingStatus,
} from 'api/useStripeAiBillingStatus'

/**
 * Parses the first action command from answer content.
 * Returns the command tag without the ! prefix (e.g., "dalle", "nb").
 */
export function parseActionCommandFromContent(content: string): string | null {
    if (!content) return null

    // Match pattern: ! followed by action tag characters, allowing internal dot separators.
    const actionCommandRegex = /!([A-Za-z0-9_-]+(?:\.[A-Za-z0-9_-]+)*)/
    const match = content.match(actionCommandRegex)

    return match ? match[1].toLowerCase() : null
}

/**
 * Parses duration from content (e.g., "7s", "10sec", "5seconds")
 * Returns the duration in seconds, or null if no duration found
 * Pattern: 1-3 digits followed by s/sec/secs/second/seconds (no spaces)
 */
export function parseDurationFromContent(content: string): number | null {
    if (!content) return null

    // Match pattern: 1-3 digits followed by duration suffix (no spaces between)
    const durationRegex = /\b(\d{1,3})(?:s|sec|secs|seconds?)\b/i
    const match = content.match(durationRegex)

    if (match) {
        const duration = parseInt(match[1], 10)
        // Sanity check: reasonable video duration range (1-999 seconds)
        if (duration >= 1 && duration <= 999) {
            return duration
        }
    }

    return null
}

/**
 * Result of duration validation
 */
export interface DurationValidationResult {
    isValid: boolean
    requestedDuration: number
    actualDuration: number
    allowedDurations?: number[]
    minDuration?: number
    maxDuration?: number
}

/**
 * Validates a requested duration against a model's constraints
 * Returns validation result with the actual duration that will be used
 */
export function validateDurationForModel(
    requestedDuration: number,
    model: IAiModel,
): DurationValidationResult {
    const allowedDurations = model.allowedDurations
    const minDuration = model.minDurationSeconds
    const maxDuration = model.maxDurationSeconds
    const defaultDuration = model.defaultDurationSeconds

    // If model has allowed_durations array, check if requested is in the list
    if (allowedDurations && allowedDurations.length > 0) {
        if (allowedDurations.includes(requestedDuration)) {
            return {
                isValid: true,
                requestedDuration,
                actualDuration: requestedDuration,
                allowedDurations,
            }
        }

        // Find the nearest allowed duration
        const nearestDuration = allowedDurations.reduce((prev, curr) => {
            return Math.abs(curr - requestedDuration) < Math.abs(prev - requestedDuration)
                ? curr
                : prev
        })

        return {
            isValid: false,
            requestedDuration,
            actualDuration: nearestDuration,
            allowedDurations,
        }
    }

    // If model has min/max constraints, clamp to range
    if (minDuration != null || maxDuration != null) {
        const min = minDuration ?? 1
        const max = maxDuration ?? 999

        if (requestedDuration >= min && requestedDuration <= max) {
            return {
                isValid: true,
                requestedDuration,
                actualDuration: requestedDuration,
                minDuration: min,
                maxDuration: max,
            }
        }

        // Clamp to valid range
        const clampedDuration = Math.max(min, Math.min(max, requestedDuration))

        return {
            isValid: false,
            requestedDuration,
            actualDuration: clampedDuration,
            minDuration: min,
            maxDuration: max,
        }
    }

    // No constraints - duration is valid as-is
    return {
        isValid: true,
        requestedDuration,
        actualDuration: requestedDuration,
    }
}

/**
 * Result of duration confirmation dialog
 */
export interface DurationConfirmationResult {
    confirmed: boolean
    selectedDuration: number
}

/**
 * Shows confirmation modal when user requests an invalid duration.
 * Offers to fix the duration to the nearest valid value.
 * Returns { confirmed: true, selectedDuration } if user accepts the fix,
 * or { confirmed: false, selectedDuration } if user cancels.
 */
export async function confirmInvalidDuration(
    model: IAiModel,
    validationResult: DurationValidationResult,
): Promise<DurationConfirmationResult> {
    const { requestedDuration, actualDuration, allowedDurations, minDuration, maxDuration } =
        validationResult

    let constraintDescription: string
    if (allowedDurations && allowedDurations.length > 0) {
        const durationList = allowedDurations.map(d => `${d}s`).join(', ')
        constraintDescription = `${model.humanName} only supports these durations: ${durationList}`
    } else if (minDuration != null && maxDuration != null) {
        constraintDescription = `${model.humanName} supports durations between ${minDuration}s and ${maxDuration}s`
    } else {
        constraintDescription = `${model.humanName} does not support ${requestedDuration}s videos`
    }

    const confirmationMessage = `You requested a ${requestedDuration}-second video, but ${constraintDescription}.`

    const shouldContinue = await confirmAsync(confirmationMessage, {
        iconType: 'video-gen',
        confirmLabel: `Use ${actualDuration}s`,
        cancelMessage: 'Cancel',
    })

    return {
        confirmed: shouldContinue,
        selectedDuration: actualDuration,
    }
}

/**
 * Checks if content contains a video action command with an invalid duration
 * and shows confirmation modal if needed.
 * Returns DurationConfirmationResult if duration was invalid and user responded,
 * or null if no validation was needed (duration valid or not applicable).
 */
export async function checkVideoDurationAndConfirm(
    content: string,
    aiModels: IAiModel[],
): Promise<DurationConfirmationResult | null> {
    // Parse action command from content
    const actionCommand = parseActionCommandFromContent(content)
    if (!actionCommand) {
        return null // No action command, proceed
    }

    // Find the model
    const model = findModelByActionTag(aiModels, actionCommand)
    if (!model) {
        return null // Unknown model, let backend handle it
    }

    // Only check video models
    if (model.modelType !== 'video') {
        return null // Not a video model, proceed
    }

    // Parse duration from content
    const requestedDuration = parseDurationFromContent(content)
    if (requestedDuration === null) {
        return null // No duration specified, uses default - proceed
    }

    // Validate duration against model constraints
    const validationResult = validateDurationForModel(requestedDuration, model)
    if (validationResult.isValid) {
        return null // Duration is valid, proceed
    }

    // Duration is invalid - show confirmation modal
    return confirmInvalidDuration(model, validationResult)
}

/**
 * Shows confirmation modal for action command generation fee
 * Returns true if user confirmed, false if cancelled
 */
type PaymentStatus = 'loading' | 'ready' | 'setup' | 'unavailable'
export type AiPaymentMode = 'bsv' | 'stripe_metered'
type PaymentChoice = AiPaymentMode | 'setup' | 'redirect' | 'cancel'

type AiPaymentLineItem = {
    key: string
    label: string
    value: string
}

type AiPaymentTrayOptions = {
    eyebrow: string
    title: string
    lineItems?: AiPaymentLineItem[]
    usdPriceStr: string
    iconType?: string | null
    effectiveModelFeeSats: number | null
    walletType?: BsvWalletType | null
    onError?: (message: string) => void
    buildMessage?: (args: {
        eyebrow: string
        title: string
        lineItems: AiPaymentLineItem[]
        effectiveModelFeeSatsStr: string | null
        bsvStatus: PaymentStatus
        usdStatus: PaymentStatus
        selectedPaymentMode: AiPaymentMode
    }) => React.ReactNode
}

const normalizeAiPaymentMode = (mode?: string | null): AiPaymentMode =>
    mode === 'stripe_metered' ? 'stripe_metered' : 'bsv'

const userDefaultAiPaymentMode = (): AiPaymentMode =>
    normalizeAiPaymentMode(
        (window?.gon?.currentUser as { ai_payment_mode?: string } | undefined)?.ai_payment_mode,
    )

const stripeStatusPayable = (status?: StripeAiBillingStatus): boolean => {
    if (status?.usd) return !!status.usd.payable
    const subStatus = status?.stripe?.subscription_status?.toString()
    const stripeLocked = !!status?.stripe?.metered_locked_at
    return (subStatus === 'active' || subStatus === 'trialing') && !stripeLocked
}

const paymentStatusLabel = (status: PaymentStatus): string => {
    if (status === 'ready') return 'ready'
    if (status === 'setup') return 'setup required'
    if (status === 'unavailable') return 'unavailable'
    return 'checking...'
}

const defaultAiPaymentMessage = ({
    eyebrow,
    title,
    effectiveModelFeeSatsStr,
    bsvStatus,
    usdStatus,
    usdPriceStr,
    selectedPaymentMode,
}: {
    eyebrow: string
    title: string
    effectiveModelFeeSatsStr: string | null
    bsvStatus: PaymentStatus
    usdStatus: PaymentStatus
    usdPriceStr: string
    selectedPaymentMode: AiPaymentMode
}) =>
    React.createElement(
        'div',
        { style: { color: 'var(--color-text-primary)' } },
        React.createElement(
            'div',
            {
                style: {
                    fontWeight: 600,
                    fontSize: '12px',
                    letterSpacing: '0.06em',
                    textTransform: 'uppercase',
                    opacity: 0.7,
                    marginBottom: '6px',
                },
            },
            eyebrow,
        ),
        React.createElement('div', { style: { fontWeight: 700, fontSize: '18px' } }, title),
        selectedPaymentMode === 'bsv' && effectiveModelFeeSatsStr
            ? React.createElement(
                  'div',
                  {
                      style: {
                          marginTop: '10px',
                          fontSize: '13px',
                          color: 'var(--color-text-secondary)',
                      },
                  },
                  `BSV ${effectiveModelFeeSatsStr} - ${paymentStatusLabel(bsvStatus)}`,
              )
            : null,
        selectedPaymentMode === 'stripe_metered'
            ? React.createElement(
                  'div',
                  {
                      style: {
                          marginTop: '10px',
                          fontSize: '13px',
                          color: 'var(--color-text-secondary)',
                      },
                  },
                  `USD ${usdPriceStr} - ${paymentStatusLabel(usdStatus)}`,
              )
            : null,
    )

export async function confirmAiPaymentTray({
    eyebrow,
    title,
    lineItems = [],
    usdPriceStr,
    iconType,
    effectiveModelFeeSats,
    walletType,
    onError,
    buildMessage,
}: AiPaymentTrayOptions): Promise<PaymentChoice> {
    return new Promise(resolve => {
        const { openConfirmTray } = useStore.getState().actions.trayActions
        const resolvedWalletType =
            walletType || useStore.getState().currentUserBsvWalletType || BsvWalletType.SHUALLET
        const bsvPayLabel = `Pay BSV ${usdPriceStr}`
        const usdPayLabel = `Pay USD ${usdPriceStr}`
        const bsvFeeSats = Number.isFinite(effectiveModelFeeSats)
            ? Number(effectiveModelFeeSats)
            : null
        const effectiveModelFeeSatsStr =
            bsvFeeSats != null ? `${bsvFeeSats.toLocaleString()} sats` : null
        const showError = onError || ((message: string) => alert(message))

        let settled = false
        let bsvStatus: PaymentStatus = bsvFeeSats == null ? 'unavailable' : 'loading'
        let usdStatus: PaymentStatus = 'loading'
        let bsvPayable = false
        let usdPayable = false
        let usdCredits = getCachedStripeAiBillingStatus()?.usd?.mode === 'credits'
        let paymentModePersistSeq = 0
        let selectedPaymentMode: AiPaymentMode = normalizeAiPaymentMode(
            getCachedStripeAiBillingStatus()?.stripe?.ai_payment_mode || userDefaultAiPaymentMode(),
        )

        const resolveOnce = (value: PaymentChoice) => {
            if (settled) return
            settled = true
            resolve(value)
        }

        const openWallet = () => {
            const store = useStore.getState()
            store.actions?.modalActions?.openWalletModal?.()
        }

        const onBsv = () => {
            if (!bsvPayable) return
            resolveOnce('bsv')
        }

        const onUsd = () => {
            if (!usdPayable) return
            resolveOnce('stripe_metered')
        }

        // The tray has closed for Stripe setup. A payment check that lands while the checkout
        // session is being created must not reopen it: in the Android app the page stays put
        // while Stripe opens in the browser, and the user would come back to a stale tray.
        let leftForSetup = false

        const onUsdSetup = async () => {
            leftForSetup = true
            const checkout = prepareExternalCheckout()
            if (checkout.requiresPopup && !checkout.popup) {
                showError('Could not open Stripe in your browser. Please try again.')
                resolveOnce('cancel')
                return
            }
            try {
                const res = await api.createStripeAiBillingCheckoutSession()
                if (res?.url) {
                    if (!openExternalCheckout(res.url, checkout)) {
                        cancelExternalCheckout(checkout)
                        showError('Could not open Stripe in your browser. Please try again.')
                        resolveOnce('cancel')
                        return
                    }
                    resolveOnce('redirect')
                    return
                }
                cancelExternalCheckout(checkout)
                showError(res?.error || 'Stripe setup required')
                resolveOnce('cancel')
            } catch (e) {
                cancelExternalCheckout(checkout)
                showError('Stripe setup required')
                resolveOnce('cancel')
            }
        }

        const persistDefaultPaymentMode = async (mode: AiPaymentMode) => {
            const seq = ++paymentModePersistSeq
            try {
                const res = await api.setStripeAiPaymentMode(mode)
                if (res?.ok && seq === paymentModePersistSeq) {
                    setCachedStripeAiBillingPaymentMode(normalizeAiPaymentMode(res.ai_payment_mode))
                    return
                }

                if (!res?.ok && seq === paymentModePersistSeq) {
                    showError(res?.error || 'Could not update default payment method')
                }
            } catch (e) {
                if (seq === paymentModePersistSeq) {
                    showError('Could not update default payment method')
                }
            }
        }

        const updateTray = () => {
            if (settled || leftForSetup) return

            const selectedStatus = selectedPaymentMode === 'stripe_metered' ? usdStatus : bsvStatus
            const selectedPayable =
                selectedPaymentMode === 'stripe_metered' ? usdPayable : bsvPayable
            const selectedLoading = selectedStatus === 'loading'
            const primaryIsUsd = selectedPaymentMode === 'stripe_metered'
            const primaryVariant = primaryIsUsd ? 'usd' : 'bsv'
            const primaryLabel = selectedLoading
                ? 'Checking payment...'
                : selectedPayable
                ? primaryIsUsd
                    ? usdPayLabel
                    : bsvPayLabel
                : primaryIsUsd
                ? usdCredits
                    ? 'Add USD credits'
                    : 'Set up card billing'
                : 'Fund BSV wallet'
            const primaryHandler = selectedPayable
                ? primaryIsUsd
                    ? onUsd
                    : onBsv
                : primaryIsUsd
                ? onUsdSetup
                : () => {
                      openWallet()
                      resolveOnce('setup')
                  }
            const setupLinks: Array<{
                label: string
                variant?: string
                disabled?: boolean
                keepOpen?: boolean
                onClick?: () => void
            }> = []

            const alternateMode: AiPaymentMode = primaryIsUsd ? 'bsv' : 'stripe_metered'
            setupLinks.push({
                label: primaryIsUsd ? 'Switch to BSV' : 'Switch to USD',
                variant: primaryIsUsd ? 'bsv' : 'usd',
                keepOpen: true,
                onClick: () => {
                    selectedPaymentMode = alternateMode
                    persistDefaultPaymentMode(alternateMode)
                    updateTray()
                },
            })

            const message = buildMessage
                ? buildMessage({
                      eyebrow,
                      title,
                      lineItems,
                      effectiveModelFeeSatsStr,
                      bsvStatus,
                      usdStatus,
                      selectedPaymentMode,
                  })
                : defaultAiPaymentMessage({
                      eyebrow,
                      title,
                      effectiveModelFeeSatsStr,
                      bsvStatus,
                      usdStatus,
                      usdPriceStr,
                      selectedPaymentMode,
                  })

            openConfirmTray(message, primaryLabel, primaryHandler, {
                iconType,
                hideCancel: true,
                onCancel: () => resolveOnce('cancel'),
                hideConfirm: false,
                confirmDisabled: selectedLoading,
                confirmVariant: primaryVariant,
                primaryActionFirst: true,
                extraActions: [],
                setupLinks,
            })
        }

        const loadBsvReadiness = async () => {
            if (bsvFeeSats == null) {
                bsvStatus = 'unavailable'
                updateTray()
                return
            }

            try {
                let walletDetails = getCachedWalletDetails(resolvedWalletType)
                if (!walletDetails?.balanceLoaded) {
                    const resolvedBalance = await waitForWalletBalance(resolvedWalletType)
                    if (resolvedBalance != null) {
                        walletDetails = getCachedWalletDetails(resolvedWalletType)
                    }
                }

                const confirmedBalance = walletDetails?.confirmedBalance ?? 0
                const estimatedTxSizeBytes = 400
                const feeRateSatsPerKb = 100
                const estimatedChainFeeSats = Math.ceil(
                    (estimatedTxSizeBytes / 1000) * feeRateSatsPerKb,
                )
                bsvPayable = confirmedBalance >= bsvFeeSats + estimatedChainFeeSats
                bsvStatus = bsvPayable ? 'ready' : 'setup'
            } catch (e) {
                bsvPayable = false
                bsvStatus = 'setup'
            }

            updateTray()
        }

        const loadUsdReadiness = async () => {
            const applyStatus = (status?: StripeAiBillingStatus) => {
                usdPayable = stripeStatusPayable(status)
                usdStatus = usdPayable ? 'ready' : 'setup'
                usdCredits = status?.usd?.mode === 'credits'
            }

            try {
                const cachedStatus = getCachedStripeAiBillingStatus()
                if (cachedStatus) {
                    applyStatus(cachedStatus)
                    updateTray()
                    if (isStripeAiBillingStatusCacheFresh()) return
                }

                const status = await refreshStripeAiBillingStatus()
                applyStatus(status)
            } catch (e) {
                usdPayable = false
                usdStatus = 'setup'
            }

            updateTray()
        }

        updateTray()
        loadBsvReadiness()
        loadUsdReadiness()
    })
}

export async function confirmActionCommandFee(
    actionCommand: string,
    isPublicContext: boolean,
    aiModels: IAiModel[],
    exchangeRate: number,
    content?: string | null,
    durationSecondsOverride?: number | null,
): Promise<boolean> {
    const model = findModelByActionTag(aiModels, actionCommand)
    const durationSeconds =
        typeof durationSecondsOverride === 'number' && Number.isFinite(durationSecondsOverride)
            ? durationSecondsOverride
            : content
            ? parseDurationFromContent(content)
            : null
    const pricingContext = {
        textCharacterCount: typeof content === 'string' ? content.length : null,
    }

    // Get appropriate cost based on context
    const publicCost = getPublicModelCostWithDuration(model, durationSeconds, pricingContext)
    const privateCost = getPrivateModelCostWithDuration(model, durationSeconds, pricingContext)
    const usdCost = isPublicContext ? publicCost : privateCost

    // No confirmation needed for free models
    if (usdCost === 0) {
        return true
    }

    const sats = usdToSats(usdCost, exchangeRate)

    // Determine icon type
    let iconType: string | null = null
    if (model && model.modelType === 'image') {
        iconType = 'image-gen'
    } else if (model && model.modelType === 'video') {
        iconType = 'video-gen'
    }

    const currentUserHasFreeAiFeature =
        window?.gon?.currentUser?.features?.some?.(feature => feature.name === 'use_ai_for_free') ||
        false
    const effectiveModelFeeSats = currentUserHasFreeAiFeature && sats != null && sats > 0 ? 1 : sats

    const modelName = model?.humanName || actionCommand
    const usdPriceStr = `$${usdCost.toFixed(2)}`
    const generationLabel =
        iconType === 'video-gen'
            ? 'Generate Video'
            : iconType === 'image-gen'
            ? 'Generate Image'
            : 'Generate'

    const choice = await confirmAiPaymentTray({
        eyebrow: generationLabel,
        title: modelName,
        usdPriceStr,
        iconType,
        effectiveModelFeeSats,
    })

    return choice === 'bsv' || choice === 'stripe_metered'
}
